<script setup lang="ts">
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@laam/ui/base';
import NeedHelp from '~/components/NeedHelp.vue';
import ScrollToTop from '~/components/ScrollToTop.vue';
import { STORE_TYPE } from '~/utils/constants';
import { useGlobalStore } from '~/stores/global';

const globalStore = useGlobalStore();

const openPersonalData = ref(false);
const openHowUse = ref(false);
const openShareDiv = ref(false);
const openRightsDiv = ref(false);
const openCookies = ref(false);
const openTpCookiesDiv = ref(false);

const personalData = ref();
const howUse = ref();
const shareDiv = ref();
const rightsDiv = ref();
const cookies = ref();
const tpCookies = ref();

const scrollToDiv = (name: string) => {
	if (name === 'Personal data') {
		openPersonalData.value = true;
		personalData.value.scrollIntoView();
	} else if (name === 'How do we use your personal data?') {
		openHowUse.value = true;
		howUse.value.scrollIntoView();
	} else if (name === 'What information we share with third parties?') {
		openShareDiv.value = true;
		shareDiv.value.scrollIntoView();
	} else if (name === 'What are your rights?') {
		openRightsDiv.value = true;
		rightsDiv.value.scrollIntoView();
	} else if (name === 'Cookies') {
		openCookies.value = true;
		cookies.value.scrollIntoView();
	} else if (name === 'Third-Party cookies') {
		openTpCookiesDiv.value = true;
		tpCookies.value.scrollIntoView();
	}
};
</script>
<template>
	<div>
		<ScrollToTop />
		<MobileHeader :show-cart="false">
			<template #title> Privacy & Cookie Policy</template>
			<template #actions>
				<div></div>
			</template>
		</MobileHeader>
		<!-- Main div -->
		<div
			class="flex mt-[59px] w-full flex-col justify-center gap-7xl overflow-y-auto overscroll-contain bg-white pb-[130px] px-3xl py-7xl"
		>
			<!-- Intro -->
			<div class="flex flex-col gap-5xl text-sm text-gray-600">
				<div>
					{{ globalStore.store.name }}
					works to ensure that your privacy is protected when using our
					services.
				</div>

				<div>
					This website is operated by {{ globalStore.store.domain }} and this
					privacy policy applies to your use of any of
					{{ globalStore.store.name }}
					websites using the domain {{ globalStore.store.domain }}.
				</div>

				<div>
					We therefore, have a policy explaining what personal information is,
					how we use the information, who has access to the data, and your
					rights regarding the information collected.
				</div>
				Your access and use of our website constitute your acceptance of our
				Privacy Policy and Terms of Use.
			</div>
			<!-- Content div -->
			<div
				class="flex w-full flex-col justify-center border-b border-gray-200 pb-7xl gap-xl"
			>
				<div class="text-lg font-semibold text-dark">Content</div>
				<div
					class="flex flex-col justify-center gap-xl text-sm text-blue-700 font-semibold"
				>
					<div @click="scrollToDiv('Personal data')">Personal data</div>
					<div @click="scrollToDiv('How do we use your personal data?')">
						How do we use your personal data?
					</div>
					<div
						@click="
							scrollToDiv('What information we share with third parties?')
						"
					>
						What information we share with third parties?
					</div>
					<div @click="scrollToDiv('What are your rights?')">
						What are your rights?
					</div>
					<div @click="scrollToDiv('Cookies')">Cookies</div>
					<div @click="scrollToDiv('Third-Party cookies')">
						Third-Party cookies
					</div>
				</div>
			</div>
			<!-- Need Help -->
			<NeedHelp
				v-if="globalStore.store.store_type === STORE_TYPE.MARKETPLACE"
			/>
			<!-- Collapsable items div -->
			<Accordion
				class="flex w-full flex-col justify-center gap-[32px]"
				type="multiple"
				collapsible
			>
				<AccordionItem v-model:open="openPersonalData" value="personalData">
					<div ref="personalData">
						<AccordionTrigger class="px-none"> Personal data </AccordionTrigger>
						<AccordionContent>
							<p class="text-gray-600 text-sm">
								We take responsibility for all the personal data (such as your
								name, address, email address, phone number, and date of birth)
								that you provide us with when you place an order on
								{{ globalStore.domain ?? globalStore.store.domain }} or
								subscribe to our newsletter or when you create your profile on
								{{ globalStore.domain ?? globalStore.store.domain }}.
							</p>
						</AccordionContent>
					</div>
				</AccordionItem>
				<AccordionItem v-model:open="openHowUse" value="howUse">
					<div ref="howUse">
						<AccordionTrigger class="px-none">
							How do we use your personal data?
						</AccordionTrigger>
						<AccordionContent>
							<p class="text-gray-600 text-sm">
								We use the information that we collect to fulfill our
								commitments to you and to provide you with the service that you
								expect. This includes sending you information and offers for
								marketing purposes. In order to provide you with relevant offers
								and information, we may analyze your personal data. We will only
								keep your data for as long as necessary to carry out our
								services to you or for as long as we are required by law. After
								this your personal data will be deleted.
							</p>
						</AccordionContent>
					</div>
				</AccordionItem>
				<AccordionItem v-model:open="openShareDiv" value="shareDiv">
					<div ref="shareDiv">
						<AccordionTrigger class="px-none">
							What information we share with third parties?
						</AccordionTrigger>
						<AccordionContent>
							<p class="text-gray-600 text-sm">
								{{ globalStore.store.name }}
								may share your personal information with third parties or
								affiliates of
								{{ globalStore.store.name }}
								who perform services on our behalf or process authorized
								transactions. The personal information we share with these
								companies to perform services on our behalf is protected via
								contractual agreements and cannot be shared. We do not sell your
								information to any third party nor do we disclose your personal
								information to unaffiliated third parties.
							</p>
						</AccordionContent>
					</div>
				</AccordionItem>
				<AccordionItem v-model:open="openRightsDiv" value="rightsDiv">
					<div ref="rightsDiv">
						<AccordionTrigger class="px-none">
							What are your rights?
						</AccordionTrigger>
						<AccordionContent>
							<p class="text-gray-600 text-sm">
								You have the right to request information about the personal
								data we hold on you. If your data is incorrect, incomplete or
								irrelevant, you can ask to have the information corrected or
								removed.
							</p>
						</AccordionContent>
					</div>
				</AccordionItem>
				<AccordionItem v-model:open="openCookies" value="cookies">
					<div ref="cookies">
						<AccordionTrigger class="px-none">Cookies</AccordionTrigger>
						<AccordionContent>
							<div class="flex flex-col gap-6 text-sm text-gray-600">
								<div>
									At
									{{ globalStore.store.name }}, we are committed to ensuring the
									highest level of service. Whilst we hope you are delighted
									with your order, we do not offer any return of exchange under
									normal conditions. We only offer Conditional Exchange if you
									face quality issues with the product.
								</div>

								<div>
									{{ globalStore.store.name }}
									urges you to file for exchange of product if reported within
									48 hours of order confirmation/ delivery. To file an
									application for exchange kindly follow below given steps:
								</div>
								<div>
									Contact our customer care via WhatsApp number, or Email Id
									provided on the website.
								</div>
								<div>
									Share the link of the outfit that you want to exchange your
									previous outfit with our customer care team.
								</div>
								<div>
									<span>
										Confirm your exchange by providing desired size guidelines
										or customizations. You will receive exchange confirmation
										within the next
									</span>
									<span class="font-semibold"> 24 to 48 hours.</span>
								</div>
							</div>
						</AccordionContent>
					</div>
				</AccordionItem>
				<AccordionItem v-model:open="openTpCookiesDiv" value="tpCookiesDiv">
					<div ref="tpCookies">
						<AccordionTrigger class="px-none">
							Third-Party cookies
						</AccordionTrigger>
						<AccordionContent>
							<p class="text-sm text-gray-600">
								We use third-party cookies to collect statistics in aggregate
								form in analysis tools such as Google Analytics. The cookies
								used are both permanent and temporary cookies (session cookies).
								The permanent cookies are stored on your computer or mobile
								device for no longer than 24 months.
							</p>
						</AccordionContent>
					</div>
				</AccordionItem>
			</Accordion>
		</div>
	</div>
</template>
